import React from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { ShieldCheck, Sparkles, ArrowUpRight, Clock, ArrowLeft } from 'lucide-react';
import { quizzesIndex, PROFESSIONAL_SIGN } from '../quizzes';

const QuizzesIndex = () => {
  return (
    <div className="min-h-screen bg-[#F4EDE2] text-[#2E2A23]">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-[#F4EDE2]/90 backdrop-blur-md border-b border-[#DBCEB4]">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <span className="w-8 h-8 rounded-full bg-[#B8806E] text-[#FAF4E8] grid place-items-center font-serif text-base leading-none">
              k
            </span>
            <span className="hidden sm:flex flex-col leading-tight">
              <span className="font-serif text-[16px] text-[#2E2A23]">Karina Negrín</span>
              <span className="text-[10px] tracking-[0.22em] uppercase text-[#8A7E6E]">
                Quizzes & reflexiones
              </span>
            </span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[12px] tracking-[0.16em] uppercase text-[#8A7E6E] hover:text-[#B8806E] transition-colors"
          >
            <ArrowLeft size={14} strokeWidth={1.7} />
            Volver al sitio
          </Link>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-5 lg:px-8 py-14 lg:py-20">
        {/* Hero */}
        <div className="max-w-[820px]">
          <div className="flex items-center gap-3 mb-5">
            <span className="inline-block w-9 h-px bg-[#8FA08A]" />
            <span className="text-[11px] tracking-[0.28em] uppercase text-[#8A7E6E]">
              Espacios de reflexión
            </span>
          </div>
          <h1 className="font-serif text-[42px] sm:text-[56px] lg:text-[68px] leading-[1.02] tracking-[-0.02em]">
            Cuestionarios para
            <span className="italic text-[#B8806E]"> escucharte.</span>
          </h1>
          <p className="mt-7 text-[17px] leading-[1.8] text-[#2E2A23]/80 font-light">
            Acá vas a encontrar herramientas pensadas con cuidado profesional. Algunas son test
            científicamente validados (como la Escala de Edimburgo, usada mundialmente). Otras son
            propuestas reflexivas para ayudarte a registrar lo que te pasa, sin etiquetas ni
            diagnósticos. Todas son confidenciales y solo vos ves tus respuestas.
          </p>

          {/* Trust block */}
          <div className="mt-7 flex flex-wrap items-center gap-x-7 gap-y-3">
            <div className="flex items-center gap-2 text-[13px] text-[#5F7259]">
              <ShieldCheck size={15} strokeWidth={1.7} />
              Confidencial · solo lo ves vos
            </div>
            <div className="flex items-center gap-2 text-[13px] text-[#8A7E6E]">
              <Sparkles size={15} strokeWidth={1.7} />
              {PROFESSIONAL_SIGN.name} · {PROFESSIONAL_SIGN.matricula}
            </div>
          </div>
        </div>

        {/* Quiz cards */}
        <div className="mt-14 grid md:grid-cols-2 gap-6">
          {quizzesIndex.map((q) => {
            const Icon = Icons[q.icon] || Sparkles;
            const isValidated = q.badge.includes('validado');
            return (
              <Link
                key={q.id}
                to={`/quizzes/${q.id}`}
                className="group bg-[#FAF4E8] border border-[#DBCEB4] rounded-[24px] p-7 lg:p-8 hover:border-[#B8806E] hover:shadow-[0_25px_60px_-30px_rgba(46,42,35,0.4)] transition-all duration-500 flex flex-col"
              >
                <div className="flex items-start justify-between gap-4 mb-5">
                  <span
                    className={`w-12 h-12 rounded-full grid place-items-center border ${
                      isValidated
                        ? 'bg-[#E5EAE0] border-[#C5D2BB] text-[#5F7259]'
                        : 'bg-[#F8E8E0] border-[#E5C8BD] text-[#B8806E]'
                    }`}
                  >
                    <Icon size={22} strokeWidth={1.5} />
                  </span>
                  <span
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10.5px] tracking-[0.16em] uppercase font-medium ${
                      isValidated
                        ? 'bg-[#E5EAE0] text-[#3F4D3D] border border-[#C5D2BB]'
                        : 'bg-[#F8E8E0] text-[#7A4F40] border border-[#E5C8BD]'
                    }`}
                  >
                    {isValidated && <ShieldCheck size={11} strokeWidth={1.8} />}
                    {q.badge}
                  </span>
                </div>

                <h2 className="font-serif text-[26px] lg:text-[30px] text-[#2E2A23] leading-[1.15] tracking-[-0.01em]">
                  {q.title}
                </h2>
                <p className="font-script text-[#B8806E] text-[20px] mt-1 leading-tight">
                  {q.subtitle}
                </p>

                <p className="mt-4 text-[14.5px] leading-[1.7] text-[#2E2A23]/75 font-light">
                  {q.description}
                </p>

                <div className="mt-6 pt-5 border-t border-[#DBCEB4] flex items-center justify-between text-[12px] text-[#8A7E6E]">
                  <span className="flex items-center gap-1.5">
                    <Clock size={12} strokeWidth={1.7} />
                    {q.duration} · {q.questionsCount} preguntas
                  </span>
                  <span className="text-[11px] uppercase tracking-[0.14em]">{q.forWho}</span>
                </div>

                <div className="mt-5 inline-flex items-center gap-2 text-[#B8806E] text-[13px] tracking-[0.16em] uppercase group-hover:gap-3 transition-all duration-300">
                  Empezar
                  <ArrowUpRight size={16} strokeWidth={1.6} />
                </div>
              </Link>
            );
          })}
        </div>

        {/* Footer disclaimer */}
        <div className="mt-16 bg-[#E5EAE0] border border-[#C5D2BB] rounded-2xl p-6 sm:p-8">
          <div className="flex items-start gap-3">
            <ShieldCheck size={20} strokeWidth={1.7} className="text-[#5F7259] shrink-0 mt-0.5" />
            <div className="text-[14px] leading-[1.75] text-[#3F4D3D] max-w-[760px]">
              <p className="font-medium mb-2">Sobre el uso de estas herramientas</p>
              <p>
                Las propuestas reflexivas que encontrás acá <strong>no son tests clínicos
                validados</strong> ni reemplazan una consulta profesional. La Escala de Edimburgo
                (EPDS) sí lo es, pero su resultado siempre debe interpretarse con un/a profesional.
                Ningún cuestionario online puede sustituir el espacio terapéutico. Si lo que
                respondiste te dejó pensando, te invito a contactarme: cada proceso es único y
                merece la mirada que solo el encuentro permite.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default QuizzesIndex;
